import type { PlatformType } from '@/editor/shared/constants/available-platforms';
import { AvailablePlatformsEnum } from '@/editor/shared/constants/available-platforms';
import type { IMediaLibraryImageModel } from '@/shared/models/media-library/media-library-image.model';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { FC } from 'react';
import { useState } from 'react';
import FacebookIcon from '@mui/icons-material/Facebook';
import SnapChatIcon from '@/shared/icons/SnapChatIcon';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import type { CreativeType } from '@/editor/shared/constants/creatives-types.enum';
import CreativeTypesEnum from '@/editor/shared/constants/creatives-types.enum';
import { useAppSelector } from '@/store/hooks/redux';
import { useTranslation } from 'react-i18next';
import GoogleIcon from '@mui/icons-material/Google';
import { ExternalCompanyIdEnum } from '@/shared/constants/external-company-id.enum';
import DownloadIcon from '@mui/icons-material/Download';
import axiosInstance from '@/shared/services/api/axios.api';

interface ImageBoxProps {
  image: IMediaLibraryImageModel;
  platform: PlatformType;
  creativeType: CreativeType;
  handleImageClick: (platform: PlatformType, image: IMediaLibraryImageModel) => void;
  handleImageOverlay: (image: IMediaLibraryImageModel) => void;
  isImageSomeWhereSelected: (image: IMediaLibraryImageModel) => boolean;
  isImageSelectedByPlatform: (platform: PlatformType) => boolean;
  handleImageForMetaEmera: (image: IMediaLibraryImageModel) => void;
}

const ImageBox: FC<ImageBoxProps> = ({
  image,
  platform,
  creativeType,
  handleImageClick,
  handleImageOverlay,
  isImageSomeWhereSelected,
  isImageSelectedByPlatform,
  handleImageForMetaEmera,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const { t } = useTranslation();

  const isOverlayAvailable = useAppSelector((state) => state.creativeStepSliceReducer.isOverlayAvailable);
  const companyId = useAppSelector((state) => state.propertyInfoSliceReducer.companyId);
  const selectedBudgetType = useAppSelector((state) => state.creativeStepSliceReducer.selectedBudgetType);

  const overlay = creativeType === CreativeTypesEnum.SINGLE && isOverlayAvailable;
  const isDefaultOverlay = useAppSelector((state) => state.creativeStepSliceReducer.isOverlayDefault);
  const isEieComingForSale =
    (companyId == ExternalCompanyIdEnum.EIE || companyId == ExternalCompanyIdEnum.EIE_VISMA) &&
    selectedBudgetType === 'coming_for_sale';

  const downloadImage = async (image: IMediaLibraryImageModel) => {
    try {
      const response = await axiosInstance.get(`medias/${image?.id}/proxy`, {
        responseType: 'blob',
      });

      if (response.statusText !== 'OK') {
        throw new Error('Network response fail');
      }

      const url = URL.createObjectURL(response.data);

      const link = document.createElement('a');
      link.href = url;
      link.download = image.fileName;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
    }
  };

  return (
    <>
      {isImageSomeWhereSelected(image) ? (
        <Box
          onClick={() => {
            handleImageClick(platform, image);
          }}>
          <Box
            sx={{
              position: 'absolute',
              zIndex: '2',
              top: '0px',
              left: '0px',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255,255,255,0.8)',
            }}>
            <Box
              sx={{
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              {isImageSelectedByPlatform(AvailablePlatformsEnum.META) && <FacebookIcon sx={{ fontSize: '32px' }} />}
              {isImageSelectedByPlatform(AvailablePlatformsEnum.GOOGLE_ADS) && <GoogleIcon sx={{ fontSize: '32px' }} />}
              {isImageSelectedByPlatform(AvailablePlatformsEnum.SNAPCHAT) && <SnapChatIcon fontSize={'large'} />}
              {isImageSelectedByPlatform(AvailablePlatformsEnum.DELTA) && <NewspaperIcon sx={{ fontSize: '32px' }} />}
              {isImageSelectedByPlatform(AvailablePlatformsEnum.BIDTHEATRE) && (
                <img
                  src="/images/bidtheatre/bidtheatre.png"
                  alt="Bidtheatre"
                  style={{ fontSize: 'large', width: '32px', height: '32px' }}
                />
              )}
              {isImageSelectedByPlatform(AvailablePlatformsEnum.BOARDS) && (
                <img
                  src="/images/boards/boards_logo.jpg"
                  alt="Boards"
                  style={{ fontSize: 'large', width: '32px', height: '32px' }}
                />
              )}
            </Box>
          </Box>
          <img
            width={'100%'}
            src={image.preSignedUrl}
            srcSet={image.preSignedUrl}
            alt={image.fileName}
            loading="lazy"
          />
        </Box>
      ) : (
        <Box
          onMouseOut={() => setVisible(false)}
          onMouseOver={() => {
            !isImageSelectedByPlatform(platform!) ? setVisible(true) : null;
          }}>
          <Box
            sx={{
              display: visible ? 'flex' : 'none',
              position: 'absolute',
              zIndex: '2',
              top: '0px',
              left: '0px',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(255,255,255,0.7)',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            <Box
              sx={{
                color: '#fff',
                flexDirection: 'column',
                display: 'flex',
              }}>
              <Button
                onClick={() => {
                  platform == AvailablePlatformsEnum.META &&
                    (companyId == ExternalCompanyIdEnum.EMERA_EIE || isEieComingForSale) &&
                    !isDefaultOverlay
                    ? handleImageForMetaEmera(image)
                    : handleImageClick(platform, image);
                  setVisible(false);
                }}
                sx={{
                  my: 1,
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#e6952f',
                    color: '#FFF',
                  },
                }}
                variant="contained"
                startIcon={<CheckCircleOutline />}>
                {t('imageSelect')}
              </Button>
              <Button
                onClick={() => {
                  downloadImage(image);
                }}
                sx={{
                  my: 1,
                  backgroundColor: '#000',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#e6952f',
                    color: '#FFF',
                  },
                }}
                variant="contained"
                startIcon={<DownloadIcon />}>
                {t('download')}
              </Button>
              {overlay && (
                <Button
                  onClick={() => {
                    handleImageOverlay(image);
                    setVisible(false);
                  }}
                  sx={{
                    my: 1,
                    backgroundColor: '#000',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#e6952f',
                      color: '#FFF',
                    },
                  }}
                  variant="contained"
                  startIcon={<AddPhotoAlternateOutlined />}>
                  {t('overlaySelect')}
                </Button>
              )}
            </Box>
          </Box>
          <img
            width={'100%'}
            src={image.preSignedUrl}
            srcSet={image.preSignedUrl}
            alt={image.fileName}
            loading="lazy"
          />
        </Box>
      )}
    </>
  );
};

export default ImageBox;
